/* eslint-disable react/style-prop-object */

import React, {
  type ComponentProps,
  memo,
  type PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import { defineMessages, FormattedMessage, FormattedNumber } from 'react-intl';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { type To } from '@react-navigation/native/lib/typescript/src/useLinkTo';
import { type Asset } from 'contentful';
import {
  getCloudinaryUrl,
  ProductCardConstants,
  ProductCardV2,
} from '@sg/garnish';
import { useProductMicroNutritionData } from '@sg/shared-fe/features/menu';
import { ProductDetails } from '@sg/shared-fe/features/product-details';

import { MenuCategoriesGrid } from '@order/components/menu';
import { ProductDetailsCardCarousel } from '@order/features/product-details';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { useDietaryRestrictions } from '../../..';
import { useOneClickAddToBag } from '../../hooks';
import {
  type MenuContentDeliveryDetailsData,
  type MenuContentProduct,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentProductCard = memo(
  (props: MenuContentProductCardProps) => {
    const {
      product,
      restaurantSlug,
      deliveryDetails,
      imageFetchingPriority,
      hoverImageUrl,
      hoverStaticImageUrl,
      hoverImageAlt,
      carousel,
      shouldShowNutritionData,
      shouldShowNutritionLink,
      generateLink,
    } = props;

    const {
      baseProduct,
      dietaryProperties,
      calories,
      isModifiable,
      name,
      description,
      outOfStock,
      asset,
      label,
      cost,
      slug,
    } = product;

    const getRestrictionNotice = useDietaryRestrictions({ referrer: 'Menu' });
    const { trackEvent } = useTelemetry();
    const { fontScale } = useWindowDimensions();
    const { track } = useTelemetry();

    // ─── Derived Data ────────────────────────────────────────────────────

    const restrictionNotice = getRestrictionNotice(dietaryProperties);

    const to = useMemo(() => generateLink(slug), [slug, generateLink]);

    const carouselItems = useMemo<ProductDetailsCardCarouselItems>(() => {
      if (!carousel || carousel.length === 0) return [];

      const baseImageItem: ProductDetailsCardCarouselItems[number] = {
        type: 'image',
        source: getCloudinaryUrl(
          product.asset.url,
          ProductCardConstants.IMAGE_TRANSFORM_CONFIG,
        ),
        alt: product.name,
        contentFit: 'contain',
      };

      const otherItems: ProductDetailsCardCarouselItems = carousel.map(
        (asset) => {
          const media = asset.fields.imageVideo;

          const isVideo = media.fields.file.contentType.startsWith('video');
          const source = media.fields.file.url;
          const staticSource = asset.fields.staticImage?.fields?.file?.url;
          const alt = media.fields.title ?? media.fields.description;

          return {
            type: isVideo ? 'video' : 'image',
            source,
            staticSource,
            alt,
          };
        },
      );

      return [baseImageItem, ...otherItems];
    }, [carousel, product.asset.url, product.name]);

    // ─── Nutrition Data ──────────────────────────────────────────────────

    const macroNutritionData = useProductMicroNutritionData({ baseProduct });
    const nutritionDataEntriesLineBreak = fontScale >= 1.3 ? 2 : undefined;

    // ─── Helpers ─────────────────────────────────────────────────────────

    const onOneClickAddToBagSuccess = useCallback<OnOneClickAddToBagSuccess>(
      (selectedProduct) => {
        track('menu.one_click_add_to_bag_submit', { selectedProduct });
      },
      [track],
    );

    const onOneClickAddToBagError = useCallback<OnOneClickAddToBagError>(
      (errorMessage) => {
        track('menu.one_click_add_to_bag_fail', { message: errorMessage });
      },
      [track],
    );

    const { addProductToBag, isAddingProductToBag } = useOneClickAddToBag({
      restaurantSlug,
      deliveryDetails,
      onSuccess: onOneClickAddToBagSuccess,
      onError: onOneClickAddToBagError,
    });

    const handleOneClickAddToBag = useCallback(async () => {
      await addProductToBag?.(product, 1);
    }, [addProductToBag, product]);

    const trackCardPress = useCallback(() => {
      const selectedProduct = telemetryProductFromInput({
        ...product,
        location: restaurantSlug,
      });

      trackEvent({
        name: 'pdp.view',
        payload: { entryPoint: 'menu', selectedProduct },
      });
    }, [product, restaurantSlug, trackEvent]);

    // ─── Flags ───────────────────────────────────────────────────────────

    const isProductAvailable = !outOfStock;
    const isProductSupportsOneClickAddToBag = !isModifiable;
    const isNavigationEnabled =
      isProductAvailable && !isProductSupportsOneClickAddToBag;

    // Customers can add products to their bags with a single click by pressing
    // the entire product card or the standard 1-click CTA button.
    const shouldUsePressableVariation = isProductAvailable && isProductSupportsOneClickAddToBag; // prettier-ignore

    const shouldRenderMacroNutritionData = shouldShowNutritionData && macroNutritionData.length > 0; // prettier-ignore
    const shouldRenderInlineCalories = !shouldShowNutritionData;
    const shouldRenderProductDetailsLink = shouldShowNutritionLink && baseProduct?.slug; // prettier-ignore

    // NOTE: On the web platform, we don't want to show the carousel
    const shouldRenderCarousel =
      Platform.OS !== 'web' && carouselItems.length > 0;

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={styles.cardContainer}>
        {shouldRenderProductDetailsLink ? (
          <ProductDetails.Link
            to={`/product-details/${baseProduct.slug}`}
            name={product.name}
            slug={baseProduct.slug}
          />
        ) : null}

        <MenuContentProductCardContainer
          to={to}
          onPress={trackCardPress}
          onQuickAddToBag={handleOneClickAddToBag}
          isLoading={isAddingProductToBag}
          isNavigationEnabled={isNavigationEnabled}
          productName={name}
          variation={shouldUsePressableVariation ? 'pressable' : 'link'}
        >
          <ProductCardV2.Container
            canHover={isNavigationEnabled || shouldUsePressableVariation}
            style={styles.container}
          >
            {!shouldRenderCarousel &&
            shouldShowNutritionLink &&
            hoverImageUrl ? (
              <ProductCardV2.HoverImage
                source={hoverImageUrl}
                staticSource={hoverStaticImageUrl}
                alt={hoverImageAlt}
              />
            ) : null}

            {!shouldRenderCarousel && asset.url ? (
              <ProductCardV2.Image
                source={asset.url}
                alt={name}
                isUnavailable={outOfStock}
                priority={imageFetchingPriority}
              />
            ) : null}

            {shouldRenderCarousel ? (
              <ProductDetailsCardCarousel items={carouselItems} />
            ) : null}

            <ProductCardV2.LabelContainer>
              {isProductAvailable && label?.name ? (
                <ProductCardV2.Label>{label.name}</ProductCardV2.Label>
              ) : null}

              {isProductAvailable ? null : (
                <ProductCardV2.UnavailableLabel>
                  <FormattedMessage {...messages.unavailableProductLabel} />
                </ProductCardV2.UnavailableLabel>
              )}
            </ProductCardV2.LabelContainer>

            <ProductCardV2.Heading>{name}</ProductCardV2.Heading>
            <ProductCardV2.Description>{description}</ProductCardV2.Description>

            {shouldRenderMacroNutritionData ? (
              <ProductCardV2.NutritionData.Stack
                lineBreakAfter={nutritionDataEntriesLineBreak}
              >
                {macroNutritionData.map((entry) => (
                  <ProductCardV2.NutritionData.Item
                    key={entry.label}
                    value={entry.value}
                    label={entry?.labelShort ?? entry.label}
                  />
                ))}
              </ProductCardV2.NutritionData.Stack>
            ) : null}

            <ProductCardV2.Footer>
              <ProductCardV2.ProductDetailsContainer>
                <ProductCardV2.ProductDetail palette="dark-kale">
                  <FormattedNumber
                    value={cost / 100}
                    style="currency"
                    currency="USD"
                  />
                </ProductCardV2.ProductDetail>

                {shouldRenderInlineCalories ? (
                  <ProductCardV2.ProductDetail>
                    <FormattedMessage
                      {...messages.caloriesText}
                      values={{ calories }}
                    />
                  </ProductCardV2.ProductDetail>
                ) : null}
              </ProductCardV2.ProductDetailsContainer>

              {restrictionNotice ? (
                <ProductCardV2.AllergenNotice>
                  {restrictionNotice}
                </ProductCardV2.AllergenNotice>
              ) : null}
            </ProductCardV2.Footer>
          </ProductCardV2.Container>
        </MenuContentProductCardContainer>

        {isProductSupportsOneClickAddToBag ? (
          <ProductCardV2.AddToBagContainer>
            <ProductCardV2.AddToBagIcon
              isLoading={isAddingProductToBag}
              isDisabled={!isProductAvailable}
            />
          </ProductCardV2.AddToBagContainer>
        ) : null}
      </View>
    );
  },
);

// ─────────────────────────────────────────────────────────────────────────────

const MenuContentProductCardContainer = (
  props: MenuContentProductCardContainerProps,
) => {
  const {
    variation,
    to,
    productName,
    isNavigationEnabled,
    isLoading,
    children,
    onPress,
    onQuickAddToBag,
  } = props;

  if (variation === 'pressable') {
    return (
      <MenuCategoriesGrid.ProductCardPressable
        onPress={onQuickAddToBag}
        isDisabled={isLoading}
        isLoading={isLoading}
        productName={productName}
      >
        {children}
      </MenuCategoriesGrid.ProductCardPressable>
    );
  }

  return (
    <MenuCategoriesGrid.ProductCardLink
      to={to}
      onPress={onPress}
      isNavigationEnabled={isNavigationEnabled}
      productName={productName}
    >
      {children}
    </MenuCategoriesGrid.ProductCardLink>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  caloriesText: {
    defaultMessage: '{calories} cals',
    description: 'Menu | Product card | Calories',
  },
  unavailableProductLabel: {
    defaultMessage: 'Currently unavailable',
    description: 'Menu | Product card | Unavailable product label',
  },
  quickAddToBagA11yLabel: {
    defaultMessage: 'Add 1 {product} to your bag',
    description: 'Menu | Product card | 1-click add to bag | a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  cardContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentProductCardProps = {
  product: MenuContentProduct;
  restaurantSlug: string;
  deliveryDetails?: MenuContentDeliveryDetailsData;
  imageFetchingPriority: ComponentProps<typeof ProductCardV2.Image>['priority'];
  hoverImageUrl: string | undefined;
  hoverStaticImageUrl: string | undefined;
  hoverImageAlt: string | undefined;
  carousel:
    | { fields: { imageVideo: Asset; staticImage?: Asset } }[]
    | undefined;
  generateLink: (productSlug: string) => To;
  shouldShowNutritionData: boolean;
  shouldShowNutritionLink: boolean;
};

type MenuContentProductCardContainerProps = PropsWithChildren<{
  variation: 'link' | 'pressable';
  to: To;
  onQuickAddToBag: () => void;
  onPress?: () => void;
  isLoading: boolean;
  isNavigationEnabled: boolean;
  productName: string;
}>;

type ProductDetailsCardCarouselItems = ComponentProps<
  typeof ProductDetailsCardCarousel
>['items'];

type OnOneClickAddToBagSuccess = NonNullable<OneClickAddToBagParams['onSuccess']>; // prettier-ignore
type OnOneClickAddToBagError = NonNullable<OneClickAddToBagParams['onError']>; // prettier-ignore

type OneClickAddToBagParams = Parameters<typeof useOneClickAddToBag>['0'];
